import React, { useEffect, useState } from 'react';
import { ArrowDownLeft, Calendar, Download } from 'lucide-react';

interface GeneralLedgerReportProps {
  activeReport: string | null;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  accounts: any[];
  contacts: any[];
  manualJournals: any[];
  invoices: any[];
  bills: any[];
  transactions: any[];
  reportAccountId: string | null;
  setReportAccountId: (id: string | null) => void;
  reportContactId: string | null;
  setReportContactId: (id: string | null) => void;
  setActiveReport: (report: string | null) => void;
  onClearInitial?: () => void;
  onNavigate?: (view: string, id?: string) => void;
  handleExport: () => void;
}

export const GeneralLedgerReport: React.FC<GeneralLedgerReportProps> = ({
  activeReport,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  accounts,
  contacts,
  manualJournals,
  invoices,
  bills,
  transactions,
  reportAccountId,
  setReportAccountId,
  reportContactId,
  setReportContactId,
  setActiveReport,
  onClearInitial,
  onNavigate,
  handleExport
}) => {
  const [localLedgerEntries, setLocalLedgerEntries] = useState<any[] | null>(null);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
    const orgId = typeof window !== 'undefined' ? localStorage.getItem('selected_org_id') : null;

    if (!token || !orgId) {
      setLocalLedgerEntries(null);
      return;
    }

    let cancelled = false;
    const params = new URLSearchParams();
    if (startDate) params.set('from', startDate);
    if (endDate) params.set('to', endDate);
    if (reportAccountId) params.set('accountId', reportAccountId);

    const fetchLocalLedger = async () => {
      try {
        const response = await fetch(
          `/api/organizations/${encodeURIComponent(orgId)}/reports/general-ledger?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Failed to fetch general ledger');

        const payload = await response.json();
        const entries = Array.isArray(payload) ? payload : payload.entries || payload.data || [];
        if (!cancelled) {
          setLocalLedgerEntries(entries.map((entry: any) => ({
            date: entry.date || entry.journal_date || '',
            accountId: entry.accountId || entry.account_id || '',
            accountName: entry.accountName || entry.account_name || '',
            description: entry.description || entry.journalDescription || entry.journal_description || '',
            reference: entry.reference || entry.journalNumber || entry.journal_number || '',
            debit: Number(entry.debit) || 0,
            credit: Number(entry.credit) || 0,
            sourceType: entry.sourceType || entry.source_type || 'Journal',
            sourceId: entry.sourceId || entry.source_id || entry.journalId || entry.journal_id || entry.id,
            contactId: entry.contactId || entry.contact_id,
            contactName: entry.contactName || entry.contact_name,
            runningBalance: Number(entry.runningBalance ?? entry.running_balance) || 0
          })));
        }
      } catch (error) {
        console.error('Error fetching local general ledger:', error);
        if (!cancelled) setLocalLedgerEntries(null);
      }
    };

    fetchLocalLedger();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, reportAccountId]);

  const isWithinRange = (date: string) => {
    return date >= startDate && date <= endDate;
  };

  const allGL: any[] = [];

  // 1. Manual Journals
  manualJournals.filter(j => isWithinRange(j.date)).forEach(j => {
    (j.lines || []).forEach((l: any) => {
      const account = accounts.find(a => a.id === l.accountId || a.code === l.accountId || a.name === l.accountName);
      const accId = account?.id || l.accountId || j.accountId;
      const accName = account?.name || l.accountName || j.accountName || l.accountId || 'General Account';
      
      allGL.push({
        date: j.date,
        accountId: accId,
        accountName: accName,
        description: l.description || j.narration,
        reference: j.reference || j.journalNumber || 'Journal',
        debit: l.debit || 0,
        credit: l.credit || 0,
        sourceType: 'Journal',
        sourceId: j.id,
        contactId: l.contactId || j.contactId,
        contactName: l.contactName || j.contactName
      });
    });
  });

  const taxAcc = accounts.find((a: any) => a.type === 'Tax' || a.name.toLowerCase().includes('tax') || a.code === '2300');
  const taxId = taxAcc?.id || 'tax_payable';
  const taxName = taxAcc?.name || 'Tax Payable';

  // 2. Invoices
  invoices.filter(inv => isWithinRange(inv.date) && inv.status !== 'Draft' && inv.status !== 'Void').forEach(inv => {
    const arAccount = accounts.find(a => a.type === 'Accounts Receivable' || a.name === 'Accounts Receivable' || a.id === 'accounts_receivable');
    const arId = arAccount?.id || 'accounts_receivable';
    const arName = arAccount?.name || 'Accounts Receivable';

    // Accounts Receivable (Debit)
    allGL.push({
      date: inv.date,
      accountId: arId,
      accountName: arName,
      description: `Invoice ${inv.invoiceNumber} - ${inv.contactName}`,
      reference: inv.invoiceNumber,
      debit: inv.totalAmount,
      credit: 0,
      sourceType: 'Invoice',
      sourceId: inv.id,
      contactId: inv.contactId,
      contactName: inv.contactName
    });

    // Tax (Credit)
    if (inv.totalTax > 0) {
      allGL.push({
        date: inv.date,
        accountId: taxId,
        accountName: taxName,
        description: `Tax on Invoice ${inv.invoiceNumber}`,
        reference: inv.invoiceNumber,
        debit: 0,
        credit: inv.totalTax,
        sourceType: 'Invoice',
        sourceId: inv.id,
        contactId: inv.contactId,
        contactName: inv.contactName
      });
    }

    // Income Lines (Credit)
    (inv.items || []).forEach((item: any) => {
      const account = accounts.find(a => 
        a.id === item.accountId || 
        a.id === item.account || 
        a.code === item.accountCode || 
        a.code === item.account ||
        a.name === item.accountName ||
        a.name === item.account
      );
      const accId = account?.id || item.accountId || 'sales_revenue';
      const accName = account?.name || item.accountName || item.account || 'Sales';
      allGL.push({
        date: inv.date,
        accountId: accId,
        accountName: accName,
        description: item.description || `Sales: ${item.name}`,
        reference: inv.invoiceNumber,
        debit: 0,
        credit: item.total || (item.quantity * item.unitPrice) || 0,
        sourceType: 'Invoice',
        sourceId: inv.id,
        contactId: inv.contactId,
        contactName: inv.contactName
      });
    });
  });

  // 3. Bills
  bills.filter(bill => isWithinRange(bill.date) && bill.status !== 'Draft' && bill.status !== 'Void').forEach(bill => {
    const apAccount = accounts.find(a => a.type === 'Accounts Payable' || a.name === 'Accounts Payable' || a.id === 'accounts_payable');
    const apId = apAccount?.id || 'accounts_payable';
    const apName = apAccount?.name || 'Accounts Payable';

    // Accounts Payable (Credit)
    allGL.push({
      date: bill.date,
      accountId: apId,
      accountName: apName,
      description: `Bill ${bill.reference || bill.id} - ${bill.contactName}`,
      reference: bill.reference || bill.id,
      debit: 0,
      credit: bill.totalAmount,
      sourceType: 'Bill',
      sourceId: bill.id,
      contactId: bill.contactId,
      contactName: bill.contactName
    });

    // Tax (Debit)
    if (bill.totalTax > 0) {
      allGL.push({
        date: bill.date,
        accountId: taxId,
        accountName: taxName,
        description: `Tax on Bill ${bill.reference || bill.id}`,
        reference: bill.reference || bill.id,
        debit: bill.totalTax,
        credit: 0,
        sourceType: 'Bill',
        sourceId: bill.id,
        contactId: bill.contactId,
        contactName: bill.contactName
      });
    }

    // Expense Lines (Debit)
    (bill.items || []).forEach((item: any) => {
      const account = accounts.find(a => 
        a.id === item.accountId || 
        a.id === item.account || 
        a.code === item.accountCode || 
        a.code === item.account ||
        a.name === item.accountName ||
        a.name === item.account
      );
      const accId = account?.id || item.accountId || 'general_expenses';
      const accName = account?.name || item.accountName || item.account || 'Expenses';
      allGL.push({
        date: bill.date,
        accountId: accId,
        accountName: accName,
        description: item.description || `Expense: ${item.name}`,
        reference: bill.reference || bill.id,
        debit: item.total || (item.quantity * item.unitPrice) || 0,
        credit: 0,
        sourceType: 'Bill',
        sourceId: bill.id,
        contactId: bill.contactId,
        contactName: bill.contactName
      });
    });
  });

  // 4. Bank Transactions
  transactions.filter(tx => isWithinRange(tx.date) && tx.status === 'Completed').forEach(tx => {
    const bankAccount = accounts.find(a => a.id === tx.accountId);
    if (!bankAccount) return;

    const arAccount = accounts.find(a => a.type === 'Accounts Receivable' || a.name === 'Accounts Receivable' || a.id === 'accounts_receivable');
    const arId = arAccount?.id || 'accounts_receivable';
    const arName = arAccount?.name || 'Accounts Receivable';

    const apAccount = accounts.find(a => a.type === 'Accounts Payable' || a.name === 'Accounts Payable' || a.id === 'accounts_payable');
    const apId = apAccount?.id || 'accounts_payable';
    const apName = apAccount?.name || 'Accounts Payable';

    const amount = Math.abs(tx.amount || 0);
    const isSpend = tx.type === 'Spend' || (tx.type === 'Payment' && tx.amount < 0) || tx.amount < 0;

    if (tx.invoiceId) {
      // Debit Bank, Credit AR
      allGL.push({
        date: tx.date,
        accountId: bankAccount.id,
        accountName: bankAccount.name,
        description: `Payment for Invoice ${tx.reference || ''} - ${tx.contactName || ''}`,
        reference: tx.reference,
        debit: amount,
        credit: 0,
        sourceType: 'Transaction',
        sourceId: tx.id,
        relatedId: tx.invoiceId,
        relatedType: 'Invoice',
        contactId: tx.contactId,
        contactName: tx.contactName
      });
      allGL.push({
        date: tx.date,
        accountId: arId,
        accountName: arName,
        description: `Payment for Invoice ${tx.reference || ''} - ${tx.contactName || ''}`,
        reference: tx.reference,
        debit: 0,
        credit: amount,
        sourceType: 'Transaction',
        sourceId: tx.id,
        relatedId: tx.invoiceId,
        relatedType: 'Invoice',
        contactId: tx.contactId,
        contactName: tx.contactName
      });
    } else if (tx.billId) {
      // Debit AP, Credit Bank
      allGL.push({
        date: tx.date,
        accountId: apId,
        accountName: apName,
        description: `Payment for Bill ${tx.reference || ''} - ${tx.contactName || ''}`,
        reference: tx.reference,
        debit: amount,
        credit: 0,
        sourceType: 'Transaction',
        sourceId: tx.id,
        relatedId: tx.billId,
        relatedType: 'Bill',
        contactId: tx.contactId,
        contactName: tx.contactName
      });
      allGL.push({
        date: tx.date,
        accountId: bankAccount.id,
        accountName: bankAccount.name,
        description: `Payment for Bill ${tx.reference || ''} - ${tx.contactName || ''}`,
        reference: tx.reference,
        debit: 0,
        credit: amount,
        sourceType: 'Transaction',
        sourceId: tx.id,
        relatedId: tx.billId,
        relatedType: 'Bill',
        contactId: tx.contactId,
        contactName: tx.contactName
      });
    } else {
      const isIncome = tx.category === 'Sales' || tx.category === 'Income' || tx.type === 'Receive' || (!isSpend && tx.amount > 0);
      const account = accounts.find(a => a.id === tx.categoryId || a.code === tx.category || a.name === tx.category);
      const otherAccId = account?.id || tx.categoryId || tx.category || (isIncome ? 'sales_revenue' : 'general_expenses');
      const otherAccName = account?.name || tx.category || (isIncome ? 'Sales' : 'Expenses');

      if (isIncome) {
        // Debit Bank, Credit Revenue/Other
        allGL.push({
          date: tx.date,
          accountId: bankAccount.id,
          accountName: bankAccount.name,
          description: tx.description || tx.reference || 'Bank Receipt',
          reference: tx.reference,
          debit: amount,
          credit: 0,
          sourceType: 'Transaction',
          sourceId: tx.id,
          contactId: tx.contactId,
          contactName: tx.contactName
        });
        allGL.push({
          date: tx.date,
          accountId: otherAccId,
          accountName: otherAccName,
          description: tx.description || tx.reference || 'Bank Receipt',
          reference: tx.reference,
          debit: 0,
          credit: amount,
          sourceType: 'Transaction',
          sourceId: tx.id,
          contactId: tx.contactId,
          contactName: tx.contactName
        });
      } else {
        // Debit Expense/Other, Credit Bank
        allGL.push({
          date: tx.date,
          accountId: otherAccId,
          accountName: otherAccName,
          description: tx.description || tx.reference || 'Bank Spend',
          reference: tx.reference,
          debit: amount,
          credit: 0,
          sourceType: 'Transaction',
          sourceId: tx.id,
          contactId: tx.contactId,
          contactName: tx.contactName
        });
        allGL.push({
          date: tx.date,
          accountId: bankAccount.id,
          accountName: bankAccount.name,
          description: tx.description || tx.reference || 'Bank Spend',
          reference: tx.reference,
          debit: 0,
          credit: amount,
          sourceType: 'Transaction',
          sourceId: tx.id,
          contactId: tx.contactId,
          contactName: tx.contactName
        });
      }
    }
  });

  let glEntries = [...(localLedgerEntries || allGL)].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  const selectedAccount = reportAccountId ? (accounts.find(a => a.id === reportAccountId || a.name === reportAccountId || a.code === reportAccountId)) : null;
  if (reportAccountId) {
    glEntries = glEntries.filter(e => 
      e.accountId === reportAccountId || 
      (selectedAccount && (e.accountId === selectedAccount.id || e.accountName === selectedAccount.name || (selectedAccount.code && e.reference === selectedAccount.code))) ||
      e.accountName === reportAccountId
    );
  }

  const selectedContact = reportContactId ? (contacts.find(c => c.id === reportContactId)) : null;
  if (reportContactId) {
    glEntries = glEntries.filter(e => e.contactId === reportContactId || (selectedContact && e.contactName === selectedContact.name));
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => {
            setActiveReport(null);
            setReportAccountId(null);
            setReportContactId(null);
            if (onClearInitial) onClearInitial();
          }}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          {activeReport === 'Account Transactions' && (
            <div className="flex gap-2">
              <select
                value={reportAccountId || ''}
                onChange={(e) => setReportAccountId(e.target.value)}
                className="bg-white border border-[#141414] px-4 py-2 rounded-full text-xs font-bold outline-none"
              >
                <option value="">All Accounts</option>
                {[...accounts].sort((a,b) => a.name.localeCompare(b.name)).map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
              <select
                value={reportContactId || ''}
                onChange={(e) => setReportContactId(e.target.value)}
                className="bg-white border border-[#141414] px-4 py-2 rounded-full text-xs font-bold outline-none"
              >
                <option value="">All Contacts</option>
                {[...contacts].sort((a,b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                  <option key={c.id} value={c.id}>{c.name || c.companyName}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">
            {reportAccountId && selectedAccount ? `${selectedAccount.name} Transactions` : 
             reportContactId && selectedContact ? `${selectedContact.name} Transactions` :
             (activeReport === 'Account Transactions' ? 'Account Transactions' : 'General Ledger')}
          </h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            From {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
          </p>
        </div>

        <div className="bg-white border border-[#141414] rounded-[32px] overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414] bg-[#141414]/5">
                <th className="p-4 text-[10px] uppercase font-bold opacity-40">Date</th>
                <th className="p-4 text-[10px] uppercase font-bold opacity-40">Account</th>
                <th className="p-4 text-[10px] uppercase font-bold opacity-40">Contact</th>
                <th className="p-4 text-[10px] uppercase font-bold opacity-40">Description</th>
                <th className="p-4 text-[10px] uppercase font-bold opacity-40">Reference</th>
                <th className="p-4 text-[10px] uppercase font-bold opacity-40 text-right">Debit</th>
                <th className="p-4 text-[10px] uppercase font-bold opacity-40 text-right">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              {glEntries.map((entry, idx) => (
                <tr key={idx} className="hover:bg-[#141414]/5 transition-colors group">
                  <td className="p-4 text-xs font-bold">{entry.date}</td>
                  <td className="p-4 text-xs">
                    <button 
                      onClick={() => {
                        setActiveReport('Account Transactions');
                        setReportAccountId(entry.accountId);
                      }}
                      className="font-bold hover:underline text-left"
                    >
                      {entry.accountName}
                    </button>
                  </td>
                  <td className="p-4 text-xs">
                    {entry.contactId ? (
                      <button 
                        onClick={() => onNavigate?.('contacts', entry.contactId)}
                        className="font-bold hover:underline text-left text-blue-600"
                      >
                        {entry.contactName}
                      </button>
                    ) : (
                      <span className="opacity-50">{entry.contactName || '-'}</span>
                    )}
                  </td>
                  <td className="p-4 text-xs italic serif opacity-70">
                    {entry.description || entry.narration}
                  </td>
                  <td className="p-4 text-xs font-mono opacity-50">
                    <button
                      onClick={() => {
                        if (entry.sourceType === 'Invoice') onNavigate?.('invoices', entry.sourceId);
                        else if (entry.sourceType === 'Bill') onNavigate?.('bills', entry.sourceId);
                        else if (entry.sourceType === 'Journal') onNavigate?.('manual_journal', entry.sourceId);
                        else if (entry.sourceType === 'Transaction') {
                          if (entry.relatedType === 'Invoice') onNavigate?.('invoices', entry.relatedId);
                          else if (entry.relatedType === 'Bill') onNavigate?.('bills', entry.relatedId);
                          else onNavigate?.('banking', entry.sourceId);
                        }
                      }}
                      className="hover:underline text-left font-bold text-[#00A09D]"
                    >
                      {entry.reference || entry.id || 'View'}
                    </button>
                  </td>
                  <td className="p-4 text-right font-mono text-xs">{entry.debit > 0 ? entry.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                  <td className="p-4 text-right font-mono text-xs">{entry.credit > 0 ? entry.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                </tr>
              ))}
              {glEntries.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-12 text-center opacity-30 italic serif">No transactions found in this period.</td>
                </tr>
              )}
            </tbody>
            {glEntries.length > 0 && (
              <tfoot>
                <tr className="bg-[#141414] text-[#E4E3E0]">
                  <td colSpan={5} className="p-4 text-right text-[10px] uppercase font-bold tracking-widest">Totals</td>
                  <td className="p-4 text-right font-mono text-xs font-bold">
                    {glEntries.reduce((s, e) => s + (e.debit || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-4 text-right font-mono text-xs font-bold">
                    {glEntries.reduce((s, e) => s + (e.credit || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};
